import { AnyAction, ThunkDispatch, createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { isError } from 'common/utils';
import { getMediaDevices } from 'devices/utils';
import { isEqual } from 'lodash-es';
import { addAppErrorMessage } from 'modules/alerts/state';

import { selectMediaDevices } from './selectors';
import { UserSettings } from './types';

import type { SelectableDeviceKind } from './utils/savedDeviceChoices';
import type { MediaDevice, MediaSettingsState } from 'devices/types';
import type { AppDispatch, RootState } from 'store';

type ThunkAPI = {
  dispatch: AppDispatch;
  state: RootState;
  extra: unknown;
  rejectWithValue: unknown;
};

type DevicesChangedOptions = {
  audioOnly?: boolean;
};

/**
 * Refresh the enumerated device list. Called on initial load and on every `devicechange` (on
 * Android Chrome, which fires none, the device manager polls instead).
 *
 * Permission resolution is NOT done here — `devices/` owns that end-to-end (ADR 0001), via
 * `resolveDevicePermissions`. This thunk is purely the device-list/selection concern.
 * `audioOnly` is retained for caller compatibility but no longer changes behaviour.
 */
const devicesChangedHandler = async (
  _options: DevicesChangedOptions | undefined,
  { dispatch, getState }: { dispatch: ThunkDispatch<RootState, unknown, AnyAction>; getState: () => RootState }
): Promise<void> => {
  try {
    const newDevices: MediaDevice[] = await getMediaDevices();

    const currentMediaDeviceIds = selectMediaDevices(getState()).map(({ deviceId }) => deviceId);
    const newDeviceIds = newDevices.map(({ deviceId }) => deviceId);
    // We do this comparison to prevent unnecessary re-renders
    if (!isEqual(currentMediaDeviceIds, newDeviceIds)) {
      dispatch(userActions.navigator.mediaDevices.devicesFound(newDevices));
    }
  } catch (error) {
    logger.error(error);

    if (isError(error)) {
      dispatch(addAppErrorMessage(error.message));
    }
  }
};

export const userActions = {
  navigator: {
    mediaDevices: {
      devicesChanged: createAsyncThunk<void, DevicesChangedOptions | undefined, ThunkAPI>(
        '[navigator mediaDevices] devices changed',
        devicesChangedHandler
      ),
      devicesFound: createAction<MediaDevice[]>('[navigator mediaDevices] devices found'),
    },
  },
  mediaSettings: {
    settingsUpdated: createAction<UserSettings & MediaSettingsState>('[mediaSettings] settings updated'),
    /**
     * Agora reported the virtual-background processor couldn't keep up and we dropped the effect.
     *
     * Its own action rather than a `setMediaSettings` call because the choice has to be cleared from
     * `localStorage` as well as from the slice: rehydrating the pre-overload value would re-apply the
     * background on the next page load and overload the same device again, so the user would meet the
     * warning on every single join. `setMediaSettings` is deliberately not persisted — it also carries
     * device selections from paths that shouldn't write to storage.
     */
    videoBackgroundShed: createAction('[mediaSettings] video background shed after processor overload'),
    /**
     * The user picked a background on a surface with no media-settings form — today the health check.
     *
     * Persisted, like `settingsUpdated`: it is a deliberate choice, so it has to survive a reload the
     * same way one made in the settings modal does. `setMediaSettings` can't serve here precisely
     * because it *isn't* persisted (see the note above), and reusing `settingsUpdated` would mean
     * inventing values for the device and audio/video fields it also carries.
     */
    videoBackgroundSelected: createAction<Pick<MediaSettingsState, 'blurLevel' | 'backgroundImageId'>>(
      '[mediaSettings] video background selected'
    ),
    /**
     * The user picked a device in a picker outside the settings form (the green room, the health
     * check, the device module). Persisted like `settingsUpdated`, so the pick wins on later visits.
     */
    deviceSelected: createAction<{ kind: SelectableDeviceKind; deviceId: string }>('[mediaSettings] device selected'),
  },
};
